/**
 * Meta Pixel Utility — Advanced Matching + Event Deduplication
 * Pixel ID: 1672445733721265
 *
 * Conversions API Access Token (for server-side use ONLY — never expose in client code):
 * EAARuoPO6dXgBRDn4dZALrreAXtbWHNUQjPZAmLbduHN3KW5TxhysWEAYPgpikZB5FyybdXTPhZANCDJwem05g8rcuShH0ZA0jdA4aIsuy5THs9RzPIGX6eZC6NmKUM7j8TXmaAeaM2sOOWvTLOsl4UiZBynqEONrvguAbenJ5uws6xxPlij5ZAZBvrThF1yK73y0XeQZDZD
 *
 * To achieve 7+/10 match quality, ensure ad links include these URL params:
 *   ?email=user@example.com&fn=Maria&ln=Garcia&ph=13055551234&ct=miami&st=fl&zp=33101
 */

const PIXEL_ID = '1672445733721265'

const PRODUCT = {
  content_name: 'Fin de las Rabietas',
  content_category: 'parenting guide',
  content_ids: ['fin-rabietas-001'],
  content_type: 'product',
  value: 6.39,
  currency: 'USD',
}

// Track which events have already fired to prevent duplicates
const firedEvents = new Set<string>()

// --- SHA-256 Hashing ---
async function sha256(value: string): Promise<string> {
  const normalized = value.trim().toLowerCase()
  if (!normalized) return ''
  const encoder = new TextEncoder()
  const data = encoder.encode(normalized)
  const hashBuffer = await crypto.subtle.digest('SHA-256', data)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')
}

// --- Generate unique event ID for deduplication with CAPI ---
function generateEventId(eventName: string): string {
  const timestamp = Date.now()
  const random = Math.random().toString(36).substring(2, 10)
  return `${eventName}_${timestamp}_${random}`
}

// --- Get or create a persistent external_id for the visitor ---
function getExternalId(): string {
  const key = 'fbp_external_id'
  let id = localStorage.getItem(key)
  if (!id) {
    id = `ext_${Date.now()}_${Math.random().toString(36).substring(2, 12)}`
    localStorage.setItem(key, id)
  }
  return id
}

// --- Extract user data from URL parameters ---
function getUrlParam(name: string): string {
  try {
    const params = new URLSearchParams(window.location.search)
    return params.get(name) || ''
  } catch {
    return ''
  }
}

// --- Build Advanced Matching data from URL params ---
async function buildAdvancedMatchingData(): Promise<Record<string, string>> {
  const data: Record<string, string> = {}

  const email = getUrlParam('email') || getUrlParam('em')
  const firstName = getUrlParam('fn') || getUrlParam('first_name')
  const lastName = getUrlParam('ln') || getUrlParam('last_name')
  const phone = getUrlParam('ph') || getUrlParam('phone')
  const city = getUrlParam('ct') || getUrlParam('city')
  const state = getUrlParam('st') || getUrlParam('state')
  const zip = getUrlParam('zp') || getUrlParam('zip')
  const country = getUrlParam('country') || 'us'

  // Hash and assign all available fields
  if (email) data.em = await sha256(email)
  if (firstName) data.fn = await sha256(firstName)
  if (lastName) data.ln = await sha256(lastName)
  if (phone) {
    // Normalize phone: digits only
    const cleanPhone = phone.replace(/\D/g, '')
    data.ph = await sha256(cleanPhone)
  }
  if (city) data.ct = await sha256(city)
  if (state) data.st = await sha256(state)
  if (zip) data.zp = await sha256(zip)

  // Country is always 'us' for this product
  data.country = await sha256(country)

  // External ID for cross-device matching
  data.external_id = await sha256(getExternalId())

  return data
}

// --- Store matching data for reuse ---
let cachedMatchingData: Record<string, string> | null = null

async function getMatchingData(): Promise<Record<string, string>> {
  if (!cachedMatchingData) {
    cachedMatchingData = await buildAdvancedMatchingData()
  }
  return cachedMatchingData
}

// --- Safe fbq caller ---
function callFbq(...args: any[]) {
  const w = window as any
  if (typeof w.fbq === 'function') {
    w.fbq(...args)
  }
}

// --- Initialize Pixel ---
export async function initPixel() {
  const w = window as any

  // Load pixel script
  if (!w.fbq) {
    const n: any = (w.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments)
    })
    if (!w._fbq) w._fbq = n
    n.push = n
    n.loaded = true
    n.version = '2.0'
    n.queue = []
    const t = document.createElement('script')
    t.async = true
    t.src = 'https://connect.facebook.net/en_US/fbevents.js'
    document.head.appendChild(t)
  }

  // Enable Automatic Advanced Matching
  callFbq('set', 'autoConfig', true, PIXEL_ID)

  // Initialize with Advanced Matching data (with fallback)
  try {
    const matchData = await getMatchingData()
    callFbq('init', PIXEL_ID, matchData)
  } catch {
    // Fallback: init without advanced matching if hashing fails
    callFbq('init', PIXEL_ID)
  }

  // Add noscript fallback
  const noscript = document.createElement('noscript')
  noscript.innerHTML = `<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=${PIXEL_ID}&ev=PageView&noscript=1" />`
  document.body.appendChild(noscript)
}

// --- Track PageView ---
export function trackPageView() {
  const eventKey = 'PageView'
  if (firedEvents.has(eventKey)) return
  firedEvents.add(eventKey)

  const eventId = generateEventId('PageView')
  callFbq('track', 'PageView', {}, { eventID: eventId })

  // Store event_id for CAPI deduplication
  storeEventForCAPI('PageView', {}, eventId)
}

// --- Track ViewContent ---
export function trackViewContent() {
  const eventKey = 'ViewContent'
  if (firedEvents.has(eventKey)) return
  firedEvents.add(eventKey)

  const eventId = generateEventId('ViewContent')
  callFbq(
    'track',
    'ViewContent',
    {
      ...PRODUCT,
    },
    { eventID: eventId },
  )

  storeEventForCAPI('ViewContent', PRODUCT, eventId)
}

// --- Track AddToCart ---
export function trackAddToCart() {
  const eventKey = 'AddToCart'
  if (firedEvents.has(eventKey)) return
  firedEvents.add(eventKey)

  const eventId = generateEventId('AddToCart')
  callFbq(
    'track',
    'AddToCart',
    {
      content_name: PRODUCT.content_name,
      content_ids: PRODUCT.content_ids,
      content_type: PRODUCT.content_type,
      value: PRODUCT.value,
      currency: PRODUCT.currency,
    },
    { eventID: eventId },
  )

  storeEventForCAPI('AddToCart', PRODUCT, eventId)
}

// --- Track InitiateCheckout ---
export function trackInitiateCheckout() {
  const eventKey = 'InitiateCheckout'
  if (firedEvents.has(eventKey)) return
  firedEvents.add(eventKey)

  const eventId = generateEventId('InitiateCheckout')
  callFbq(
    'track',
    'InitiateCheckout',
    {
      content_name: PRODUCT.content_name,
      content_ids: PRODUCT.content_ids,
      num_items: 1,
      value: PRODUCT.value,
      currency: PRODUCT.currency,
    },
    { eventID: eventId },
  )

  storeEventForCAPI('InitiateCheckout', { ...PRODUCT, num_items: 1 }, eventId)
}

// --- Store event data for future CAPI server-side deduplication ---
function storeEventForCAPI(eventName: string, data: any, eventId: string) {
  try {
    const events = JSON.parse(sessionStorage.getItem('fb_capi_events') || '[]')
    events.push({
      event_name: eventName,
      event_id: eventId,
      event_time: Math.floor(Date.now() / 1000),
      event_source_url: window.location.href,
      user_data: {
        external_id: getExternalId(),
        client_user_agent: navigator.userAgent,
        fbc: getCookie('_fbc') || '',
        fbp: getCookie('_fbp') || '',
      },
      custom_data: data,
    })
    sessionStorage.setItem('fb_capi_events', JSON.stringify(events))
  } catch {
    // Silently fail if storage is unavailable
  }
}

// --- Cookie helper ---
function getCookie(name: string): string {
  try {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'))
    return match ? match[2] : ''
  } catch {
    return ''
  }
}

// --- Handle CTA click: fires AddToCart + InitiateCheckout then redirects ---
export function handlePurchaseCTA() {
  trackAddToCart()
  trackInitiateCheckout()

  // Small delay to ensure pixel events fire before navigation
  setTimeout(() => {
    window.location.href = 'https://dobolia.com/cart/64131354362205:1'
  }, 300)
}
